import { MemreSnackbar } from 'components/core';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import newLookMappings from './newLookMappings';
import { AnalyticsService } from 'utils/AnalyticsService';

type ToggleProps = {
  heading?: string;
  message?: string;
  cb?: () => void;
  learnMoreLink?: string;
};

type MatchResult = {
  type: 'old' | 'new';
  path: string;
  learnMoreLink?: string;
  hardRedirect?: boolean;
};

export default function ToggleNewLook() {
  const history = useHistory();
  const location = useLocation();
  const path = location.pathname;

  const [props, setProps] = useState<ToggleProps>({});

  useEffect(() => {
    const timer = setTimeout(() => {
      // The helper function now returns the learnMoreLink (if defined) from the mapping.
      const matchMapping = (mappings: typeof newLookMappings, currentPath: string): MatchResult | null => {
        const trimmedPath = currentPath.replace(/\/$/, '');

        for (const mapping of mappings) {
          const oldPathPattern = mapping.oldPath.replace(/\[(\w+)\]/g, '([^/]+)');
          const newPathPattern = mapping.newPath.replace(/\[(\w+)\]/g, '([^/]+)');

          const oldPathRegex = new RegExp(`^${oldPathPattern}$`);
          const newPathRegex = new RegExp(`^${newPathPattern}$`);

          const oldMatch = trimmedPath.match(oldPathRegex);
          const newMatch = trimmedPath.match(newPathRegex);

          if (oldMatch) {
            const newPath = replaceTokens(mapping.newPath, mapping.oldPath, oldMatch);
            return {
              type: 'old',
              path: newPath,
              learnMoreLink: mapping.learnMoreLink,
              hardRedirect: mapping.hardRedirect,
            };
          }

          if (newMatch) {
            const oldPath = replaceTokens(mapping.oldPath, mapping.newPath, newMatch);
            return {
              type: 'new',
              path: oldPath,
              learnMoreLink: mapping.learnMoreLink,
              hardRedirect: mapping.hardRedirect,
            };
          }
        }
        return null;
      };

      const match = matchMapping(newLookMappings, path);

      if (match) {
        setProps({
          message:
            match.type === 'new'
              ? 'Looking for the classic experience? Click to go back'
              : 'Want to see the updated look? Click to try it out',
          heading: match.type === 'new' ? 'Updated Page Design' : 'New Page Design Available',
          cb: () => handleClick(match.path, match.hardRedirect),
          learnMoreLink: match.learnMoreLink,
        });
      } else {
        setProps({});
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [path]);

  const handleClick = (url: string, isHardRedirect = false) => {
    const button_type = url.includes('v4') ? 'go to v4 page' : 'go to legacy page';
    AnalyticsService.getInstance().track('button_clicked', { button_type });

    if (isHardRedirect) {
      document.location = url;
    } else {
      history.push(url);
    }
  };

  return (
    <MemreSnackbar message={props.message} heading={props.heading} cb={props.cb} learnMoreLink={props.learnMoreLink} />
  );
}

/**
 * Replaces all occurrences of tokens in a template string
 * @param template The string template to fill with values
 * @param sourceTemplate The source template that was matched against
 * @param matches The regex match results from the source template
 * @returns The template with all token instances replaced
 */
function replaceTokens(template: string, sourceTemplate: string, matches: RegExpMatchArray): string {
  // Extract token names from the source template
  const tokenNames: string[] = [];
  let match;
  const regex = /\[(\w+)\]/g;
  while ((match = regex.exec(sourceTemplate)) !== null) {
    tokenNames.push(match[1]);
  }

  // Create a mapping of token names to values from the matches
  const tokenValues: Record<string, string> = {};
  const tokens = matches.slice(1);
  tokenNames.forEach((name, index) => {
    tokenValues[name] = tokens[index] || '';
  });

  // Replace all occurrences of each token in the template
  let result = template;
  Object.entries(tokenValues).forEach(([name, value]) => {
    result = result.replace(new RegExp(`\\[${name}\\]`, 'g'), value);
  });

  return result;
}
